import React from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { Notification } from '@ynap/notification';
import { PriceWithPromotions as Price } from '@ynap/price';
import { CombinedImage } from '@ynap/image';
import { StopPropagation } from '@ynap/overlay';
import { SingleBadge } from '@ynap/badge';
import Skeleton from '@ynap/skeleton';
import { OptimizelyFeature } from '@ynap/optimizely-utils';
/**
 * By using url fragments [href="#"] browser
 * scrolls window to top, which we want to prevent
 * Using the capture phase to ensure any clicks inside the elem don't
 * mess with this preventing
 * @param {Event} e
 */
const preventAnchorToTop = {
    onClickCapture: (e) => {
        e.preventDefault();
    },
};
const bem = new BEMHelper('LineItem');

const propTypes = {
    messages: PropTypes.shape({
        lineItem: PropTypes.shape({
            quantity: PropTypes.func.isRequired,
        }),
        product: PropTypes.shape({
            infoUnavailable: PropTypes.func.isRequired,
        }),
        considered: PropTypes.shape({
            badge: PropTypes.func.isRequired,
        }),
        basket: PropTypes.shape({
            jit: PropTypes.shape({
                lineItemMessage1: PropTypes.func.isRequired,
                lineItemMessage2: PropTypes.func.isRequired,
            }),
        }),
    }),
    image: PropTypes.string,
    brand: PropTypes.string,
    name: PropTypes.string,
    colour: PropTypes.string,
    size: PropTypes.string,
    quantity: PropTypes.string,
    price: PropTypes.object.isRequired,
    orderLevelDiscounts: PropTypes.arrayOf(PropTypes.string),
    badges: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        }),
    ),
    description: PropTypes.string,
    className: PropTypes.string,
    priceComponent: PropTypes.node,
    onClick: PropTypes.func,
    children: PropTypes.node,
    subClassName: PropTypes.shape({
        content: PropTypes.string,
        actions: PropTypes.string,
        inline: PropTypes.bool,
        unavailable: PropTypes.bool,
    }),
    isLegacyOrder: PropTypes.string,
    productId: PropTypes.string,
    productUrl: PropTypes.string,
    consideredBadge: PropTypes.string,
    showUsTaxUi: PropTypes.bool,
    isJIT: PropTypes.bool,
    locale: PropTypes.object,
};

const defaultProps = {
    priceComponent: Price,
    subClassName: {},
    showUsTaxUi: false,
};

export default function LineItem(props) {
    const {
        messages,
        image,
        brand,
        name,
        colour,
        size,
        quantity,
        price,
        orderLevelDiscounts,
        badges = [],
        className,
        priceComponent: PriceComponent,
        onClick,
        children,
        isLegacyOrder,
        subClassName: { inline, content, actions, unavailable },
        productId,
        productUrl,
        consideredBadge,
        isJIT,
        showUsTaxUi,
        cartLimitedQtyWarning,
        locale,
    } = props;

    const badgesByType = badges.reduce((accumulator, badge) => {
        const { type, label } = badge;
        if (type) {
            if (!accumulator[type]) {
                accumulator[type] = [];
            }
            accumulator[type].push(label);
        }
        return accumulator;
    }, {});

    const productCatalog = isLegacyOrder === 'true' || (brand && (colour || size)) ? true : false;

    const fieldsAvailable = brand || name;
    const DetailsComponent = onClick ? 'a' : 'div';

    // TODO: see ISCC-464, instead of just using this array to detemine whether or not to show the cumulative % discount on a per line itm basis,
    // we should use this, once WCS provides further % values for each adjustment, to really show the discounts itemised for each line item.
    const hasOrderLevelDiscount = orderLevelDiscounts?.length > 0;

    const showLimitedQtyWarning = cartLimitedQtyWarning?.parameters[0] === props?.partNumber;

    return (
        <li className={bem(null, { link: !!onClick }, className)}>
            {!productCatalog && <Notification className={bem('unavailable')} type="info" message={messages.product.infoUnavailable()} />}
            <DetailsComponent className={bem('product-details', { inline })} {...(onClick && { ...preventAnchorToTop, onClick, href: productUrl })}>
                <div className={bem('imageContainer', { unavailable })}>
                    <CombinedImage src={image?.url || image} primarySrcset={image?.srcSet} alt={brand} computeAttributes={false} imageSrcsetSize={'120px'} />
                </div>
                <div className={bem('details')}>
                    {isJIT && (
                        <Notification
                            type="delivery"
                            className={bem('jitMessage')}
                            message={[messages.basket.jit?.lineItemMessage1?.({ quantity }), messages.basket.jit?.lineItemMessage2?.()]}
                            messageModifier="flagIconBack"
                        />
                    )}
                    {productCatalog || fieldsAvailable ? (
                        <>
                            <span className={bem('brand')}>{brand}</span>
                            <span className={bem('name')}>{name}</span>
                            {consideredBadge && (
                                <span className={bem('considered')}>
                                    <SingleBadge
                                        className={bem('consideredBadge')}
                                        considered={true}
                                        badge={{ label: consideredBadge, key: 'CONSIDERED_BADGE' }}
                                        translation={messages.considered.badge()}
                                    />
                                </span>
                            )}
                            {colour && <span className={bem('colour')}>{colour}</span>}
                            {size && <span className={bem('size', { showDiamond: !!colour })}>{size}</span>}
                            {quantity && quantity > 0 ? (
                                <span className={bem('quantity')}>
                                    {messages.lineItem.quantity()}
                                    {quantity}
                                </span>
                            ) : null}
                        </>
                    ) : (
                        <Skeleton className={bem('text')} containerClassName={bem('noProduct')} count={3} align="left" />
                    )}
                    <PriceComponent
                        className={bem('price')}
                        {...price}
                        hasOrderLevelDiscount={hasOrderLevelDiscount}
                        messages={messages}
                        showUsTaxUi={showUsTaxUi}
                        designerName={brand}
                        badgeKey={badges[0]?.key}
                        country={locale?.country}
                    />
                    {Object.keys(badgesByType).map(
                        (type) => type && <SingleBadge className={bem('badges')} key={type} badge={{ label: badgesByType[type], key: type }} />,
                    )}
                </div>
                {children && (
                    <StopPropagation className={bem('actions', { inline }, actions)}>
                        {content ? <div className={content}>{children}</div> : children}
                    </StopPropagation>
                )}
            </DetailsComponent>
            <meta itemProp="productID" content={productId} /> {/* product info for personal shopper chrome extension */}
            {showLimitedQtyWarning && <Notification className={bem('limitedQtyWarning')} message={[messages?.basket?.cartLimitedQtyWarning()]} />}
        </li>
    );
}

LineItem.propTypes = propTypes;
LineItem.defaultProps = defaultProps;
